import React from "react";
import { motion } from "framer-motion";
import { Mail, MessageSquare, Database, Calendar, Webhook, Briefcase } from "lucide-react";

/*
 * AIAgentNetwork — satellites now ACTUALLY ORBIT around the center.
 * Outer container rotates continuously (linear); each satellite
 * counter-rotates so icons stay upright. Connection lines & data
 * packets are also inside the rotating group so they follow.
 */
export default function AIAgentNetwork() {
  const satellites = [
    { name: "Email",    icon: Mail,         angle:  -90, label: "Gmail" },
    { name: "Chat",     icon: MessageSquare, angle:  -30, label: "Slack" },
    { name: "CRM",      icon: Briefcase,    angle:   30, label: "HubSpot" },
    { name: "Calendar", icon: Calendar,     angle:   90, label: "GCal" },
    { name: "DB",       icon: Database,     angle:  150, label: "Postgres" },
    { name: "API",      icon: Webhook,      angle:  210, label: "Webhook" },
  ];

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Static guide rings (don't rotate) */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="-100 -100 200 200">
        <defs>
          <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#73100D" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#73100D" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="0" cy="0" r="22" fill="url(#coreGlow)" />
        {[20, 32, 44, 60, 76].map((r, i) => (
          <circle key={r} cx="0" cy="0" r={r}
            fill="none"
            stroke="#73100D"
            strokeOpacity={i % 2 === 0 ? 0.10 : 0.06}
            strokeWidth="0.25"
            strokeDasharray={i === 4 ? "1 1.6" : "none"} />
        ))}
      </svg>

      {/* Rotating group: satellites + their connection lines + packets */}
      <motion.div
        className="absolute inset-0 will-change-transform"
        animate={{ rotate: 360 }}
        transition={{ duration: 36, repeat: Infinity, ease: "linear" }}
      >
        {/* Connection lines + animated data packets (in same rotating frame) */}
        <svg className="absolute inset-0 w-full h-full" viewBox="-100 -100 200 200">
          <defs>
            <linearGradient id="linkGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%"   stopColor="#73100D" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#98413F" stopOpacity="0.45" />
            </linearGradient>
          </defs>
          {satellites.map((s, i) => {
            const rad = (s.angle * Math.PI) / 180;
            const r = 76; // satellite orbit radius (matches outer ring)
            const x = Math.cos(rad) * r;
            const y = Math.sin(rad) * r;
            // Slight curve
            const px = (x / 2) - (y * 0.18);
            const py = (y / 2) + (x * 0.18);
            const path = `M 0 0 Q ${px} ${py} ${x} ${y}`;
            const pid = `path-${i}`;
            const reverse = i % 2 === 0;
            return (
              <g key={i}>
                <path id={pid} d={path} fill="none" stroke="url(#linkGrad)" strokeWidth="0.55" strokeLinecap="round" />
                <circle r="1.4" fill="#73100D">
                  <animateMotion dur={`${3 + i * 0.2}s`} repeatCount="indefinite" rotate="auto" keyPoints={reverse ? "1;0" : "0;1"} keyTimes="0;1">
                    <mpath href={`#${pid}`} />
                  </animateMotion>
                </circle>
                <circle r="1.0" fill="#98413F" opacity="0.9">
                  <animateMotion dur={`${3 + i * 0.2}s`} begin={`${i * 0.4}s`} repeatCount="indefinite" rotate="auto" keyPoints={reverse ? "1;0" : "0;1"} keyTimes="0;1">
                    <mpath href={`#${pid}`} />
                  </animateMotion>
                </circle>
              </g>
            );
          })}
        </svg>

        {/* Satellites (positioned in % around center, counter-rotated so icons stay upright) */}
        {satellites.map((s, i) => {
          const rad = (s.angle * Math.PI) / 180;
          // Outer radius in %: ring is at r=76 in viewBox 200 -> 38% from center
          const offset = 38;
          const left = 50 + Math.cos(rad) * offset;
          const top  = 50 + Math.sin(rad) * offset;
          return (
            <div
              key={s.name}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${left}%`, top: `${top}%` }}
            >
              {/* Counter-rotate so the chip stays upright while orbiting */}
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 36, repeat: Infinity, ease: "linear" }}
              >
                <Satellite icon={s.icon} label={s.label} delay={i * 0.4} />
              </motion.div>
            </div>
          );
        })}
      </motion.div>

      {/* Central AI Core (does NOT rotate so it stays anchored) */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          className="relative w-28 h-28 grid place-items-center"
        >
          <span className="absolute inset-0 rounded-2xl border border-[#73100D]/40 animate-ping-soft" />
          <span className="absolute inset-1 rounded-2xl border border-[#73100D]/25" />
          <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-[#73100D] to-[#5E0000] grid place-items-center brand-glow border border-[#5E0000]">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/10 via-transparent to-transparent" />
            <div className="relative text-center">
              <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-white/70">RSP</div>
              <div className="text-base font-bold text-white">Agent</div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Satellite({ icon: Icon, label, delay = 0 }) {
  return (
    <div className="relative flex flex-col items-center gap-1">
      <span
        className="absolute -inset-1.5 rounded-2xl border border-[#73100D]/35 animate-ping-soft"
        style={{ animationDelay: `${delay}s` }}
      />
      <motion.div
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", delay }}
        className="relative w-11 h-11 rounded-xl bg-white border border-[#E5E5E5] grid place-items-center soft-shadow"
      >
        <Icon className="w-4.5 h-4.5 text-[#73100D]" />
      </motion.div>
      <div className="px-1.5 py-0.5 rounded bg-white/85 backdrop-blur border border-[#E5E5E5] text-[9px] font-mono font-semibold text-[#444]">
        {label}
      </div>
    </div>
  );
}
